import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Building, MapPin, Check, Loader2 } from 'lucide-react'
import PropTypes from 'prop-types'
import BranchService from '../../services/branchService'
import { isManagerRole } from '../../config/roles'

const BranchSelectionModal = ({ isOpen, onClose, onBranchSelect, userRole, user, title, description }) => {
  const [branches, setBranches] = useState([])
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Use ref to avoid infinite re-renders
  const onBranchSelectRef = useRef(onBranchSelect)
  
  // Update ref when onBranchSelect changes
  useEffect(() => {
    onBranchSelectRef.current = onBranchSelect
  }, [onBranchSelect])

  // Fetch branches when modal opens
  useEffect(() => {
    // Only fetch when modal opens, not when it closes
    if (!isOpen) {
      // Remove body class when modal closes
      document.body.classList.remove('branch-modal-open')
      // Reset state when modal closes
      setBranches([])
      setSelectedBranch(null)
      setError(null)
      return
    }

    // Don't fetch if we don't have user data yet (prevents infinite loop)
    if (!user && userRole !== 'admin' && userRole !== 'manager') {
      setLoading(true)
      return
    }

    const fetchBranches = async () => {
      try {
        setLoading(true)
        setError(null)
        console.log('BranchSelectionModal: Fetching branches for user role:', user?.role || userRole)
        
        const response = await BranchService.getAllBranches()
        
        if (response && response.success) {
          // Filter only active branches and only show Outlet types (hide warehouse)
          let activeBranches = response.data.branches.filter(branch => 
            branch.isActive && branch.type === 'outlet'
          )
          
          // If user is a manager, only show their assigned branch
          const currentUserRole = user?.role || userRole
          if (isManagerRole(currentUserRole) && user?.branch) {
            activeBranches = activeBranches.filter(branch => branch._id === user.branch._id)
          }
          
          setBranches(activeBranches)
          
          // If manager and only one outlet branch, auto-select and close modal
          if (isManagerRole(currentUserRole) && activeBranches.length === 1) {
            setSelectedBranch(activeBranches[0])
            // Automatically proceed with this branch
            setTimeout(() => {
              onBranchSelectRef.current(activeBranches[0])
            }, 100)
          }
        } else {
          setError('Failed to fetch branches')
        }
      } catch (error) {
        console.error('Error fetching branches:', error)
        setError('Failed to load branches. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    // Add body class when modal opens
    document.body.classList.add('branch-modal-open')
    fetchBranches()

    // Cleanup function to remove class if component unmounts
    return () => {
      document.body.classList.remove('branch-modal-open')
    }
  }, [isOpen, user, userRole])

  const fetchBranchesRetry = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await BranchService.getAllBranches()
      
      if (response && response.success) {
        // Filter only active branches and only show Outlet types (hide warehouse)
        let activeBranches = response.data.branches.filter(branch => 
          branch.isActive && branch.type === 'outlet'
        )
        
        // If user is a manager, only show their assigned branch
        if (isManagerRole(user?.role) && user?.branch?._id) {
          activeBranches = activeBranches.filter(branch => branch._id === user.branch._id)
        }
        
        setBranches(activeBranches)
      } else {
        setError('Failed to fetch branches')
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      setError('Failed to load branches. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch)
  }

  const handleConfirmSelection = () => {
    if (selectedBranch) {
      onBranchSelect(selectedBranch)
      onClose()
    }
  }

  if (!isOpen) return null

  const modalStyle = {
    zIndex: 2147483647, // Maximum z-index value
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transform: 'none',
    filter: 'none',
    perspective: 'none',
    contain: 'none',
    isolation: 'isolate'
  }

  const modalContent = (
    <div 
      className="branch-selection-modal bg-black bg-opacity-60 flex items-center justify-center p-4" 
      style={{
        ...modalStyle,
        pointerEvents: 'auto'
      }}
    >
      <div 
        className="branch-selection-modal-content bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl"
        style={{ 
          zIndex: 2147483647,
          transform: 'none',
          filter: 'none',
          position: 'relative',
          pointerEvents: 'auto'
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Building className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{title || 'Select Branch'}</h2>
                <p className="text-orange-100 text-sm">
                  {description || 'Choose a branch to access the POS system'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading branches...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Branches</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchBranchesRetry}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : branches.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Outlets</h3>
              <p className="text-gray-600">There are no active outlet branches available.</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-gray-600">
                  Select the branch you want to access. As a {userRole}, you can access any branch.
                </p>
              </div>

              {/* Branch List - 2 Column Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {branches.map((branch) => (
                  <div
                    key={branch._id}
                    onClick={() => handleBranchSelect(branch)}
                    className={`p-4 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                      selectedBranch?._id === branch._id
                        ? 'border-orange-500 bg-orange-50 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          selectedBranch?._id === branch._id
                            ? 'bg-orange-200 text-orange-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Building className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-base truncate">
                            {branch.name}
                          </h3>
                          {branch.type && (
                            <p className="text-sm text-gray-500 mb-1 capitalize">
                              {branch.type}
                            </p>
                          )}
                          {branch.address && (
                            <div className="flex items-start space-x-1 text-sm text-gray-600">
                              <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2 text-xs">{branch.address}</span>
                            </div>
                          )}
                          {branch.phoneNumber && (
                            <p className="text-xs text-gray-600 mt-1">
                              📞 {branch.phoneNumber}
                            </p>
                          )}
                        </div>
                      </div>
                      {selectedBranch?._id === branch._id && (
                        <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSelection}
                  disabled={!selectedBranch}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    selectedBranch
                      ? 'bg-orange-600 hover:bg-orange-700 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Access POS System
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )

  // Use createPortal to render modal at the root level, ensuring it appears above everything
  // Create a new div element if it doesn't exist to ensure proper isolation
  let modalRoot = document.getElementById('modal-root')
  if (!modalRoot) {
    modalRoot = document.createElement('div')
    modalRoot.id = 'modal-root'
    modalRoot.style.position = 'fixed'
    modalRoot.style.top = '0'
    modalRoot.style.left = '0'
    modalRoot.style.right = '0'
    modalRoot.style.bottom = '0'
    modalRoot.style.zIndex = '2147483647'
    modalRoot.style.pointerEvents = 'none'
    document.body.appendChild(modalRoot)
  }
  
  return createPortal(modalContent, modalRoot)
}

BranchSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBranchSelect: PropTypes.func.isRequired,
  userRole: PropTypes.string.isRequired,
  user: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
}

export default BranchSelectionModal
